import type { AccumulatorScheduleLine as ForwardAccumulatorScheduleLine } from '@/store/forwardAccumulator/state.ts';
import type { AccumulatorScheduleLine } from '@/store/targetAccumulator/state.ts';

import { type AccumulatorType, type Cleaners, liftToOptionalString, type RowPredicate } from './common';

const parseNumber = (value: string | undefined): number | null => {
  if (!value || value.trim() === '') {
    return null;
  }
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export type ImportedScheduledRow =
  | ImportedTargetVanillaScheduleRow
  | ImportedTargetPivotScheduleRow
  | ImportedTargetEkiScheduleRow
  | ImportedTargetEkiPivotScheduleRow
  | ImportedFowardScheduleRow;

export type ScheduleImportError = never;

export const validators: Record<ScheduleImportError, RowPredicate> = {};

export const mapScheduleRow = ({ cleanupNumber, sanitizeDate, accuType }: Cleaners) => {
  switch (accuType) {
    case 'TargetAccu':
      return mapTargetVanillaScheduleRow({ cleanupNumber, sanitizeDate });
    case 'EKI':
      return mapTargetEkiScheduleRow({ cleanupNumber, sanitizeDate });
    case 'PIVOTEKI':
      return mapTargetEkiPivotScheduleRow({ cleanupNumber, sanitizeDate });
    case 'PIVOT':
      return mapTargetPivotScheduleRow({ cleanupNumber, sanitizeDate });
    case 'Permanent':
    case 'Standard':
      return mapForwardScheduleRow({ cleanupNumber, sanitizeDate });

    default:
      return mapTargetVanillaScheduleRow({ cleanupNumber, sanitizeDate });
  }
};

// ████████╗ █████╗ ██████╗  ██████╗ ███████╗████████╗    ██╗   ██╗ █████╗ ███╗   ██╗██╗██╗     ██╗      █████╗
// ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝ ██╔════╝╚══██╔══╝    ██║   ██║██╔══██╗████╗  ██║██║██║     ██║     ██╔══██╗
//    ██║   ███████║██████╔╝██║  ███╗█████╗     ██║       ██║   ██║███████║██╔██╗ ██║██║██║     ██║     ███████║
//    ██║   ██╔══██║██╔══██╗██║   ██║██╔══╝     ██║       ╚██╗ ██╔╝██╔══██║██║╚██╗██║██║██║     ██║     ██╔══██║
//    ██║   ██║  ██║██║  ██║╚██████╔╝███████╗   ██║        ╚████╔╝ ██║  ██║██║ ╚████║██║███████╗███████╗██║  ██║
//    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝   ╚═╝         ╚═══╝  ╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝╚══════╝╚══════╝╚═╝  ╚═╝

enum Col {
  FixingDate = 0,
  SettlementDate = 1,
  Strike = 2,
  Notional = 3,
  LeverageAmount = 4,
}
export interface ImportedTargetVanillaScheduleRow {
  fixingDate: string;
  settlementDate: string | undefined;
  strike: string;
  notional: string;
  leverageAmount: string | undefined;
}

export const mapTargetVanillaScheduleRow =
  ({ cleanupNumber, sanitizeDate }: Cleaners) =>
  (row: readonly any[]): ImportedTargetVanillaScheduleRow => ({
    fixingDate: sanitizeDate(row[Col.FixingDate]),
    settlementDate: liftToOptionalString(sanitizeDate)(row[Col.SettlementDate]),
    strike: cleanupNumber(row[Col.Strike]),
    notional: cleanupNumber(row[Col.Notional], true),
    leverageAmount: liftToOptionalString(cleanupNumber, true)(row[Col.LeverageAmount]),
  });

export const requiredVanillaTargetColumns = ['Fixing date', 'Settlement date', 'Strike', 'Notional', 'Leverage'];

// ██████╗ ██╗██╗   ██╗ ██████╗ ████████╗    ████████╗ █████╗ ██████╗  ██████╗ ███████╗████████╗
// ██╔══██╗██║██║   ██║██╔═══██╗╚══██╔══╝    ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝ ██╔════╝╚══██╔══╝
// ██████╔╝██║██║   ██║██║   ██║   ██║          ██║   ███████║██████╔╝██║  ███╗█████╗     ██║
// ██╔═══╝ ██║╚██╗ ██╔╝██║   ██║   ██║          ██║   ██╔══██║██╔══██╗██║   ██║██╔══╝     ██║
// ██║     ██║ ╚████╔╝ ╚██████╔╝   ██║          ██║   ██║  ██║██║  ██║╚██████╔╝███████╗   ██║
// ╚═╝     ╚═╝  ╚═══╝   ╚═════╝    ╚═╝          ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝   ╚═╝

enum ColTargetPivot {
  FixingDate = 0,
  SettlementDate = 1,
  StrikeUp = 2,
  Pivot = 3,
  StrikeDown = 4,
  Notional = 5,
  LeverageAmount = 6,
}

export interface ImportedTargetPivotScheduleRow {
  fixingDate: string;
  settlementDate: string | undefined;
  strikeUp: string;
  step: string;
  strikeDown: string;
  notional: string;
  leverageAmount: string | undefined;
}

export const mapTargetPivotScheduleRow =
  ({ cleanupNumber, sanitizeDate }: Cleaners) =>
  (row: readonly any[]): ImportedTargetPivotScheduleRow => ({
    fixingDate: sanitizeDate(row[ColTargetPivot.FixingDate]),
    settlementDate: liftToOptionalString(sanitizeDate)(row[ColTargetPivot.SettlementDate]),
    strikeDown: cleanupNumber(row[ColTargetPivot.StrikeDown]),
    step: cleanupNumber(row[ColTargetPivot.Pivot]),
    strikeUp: cleanupNumber(row[ColTargetPivot.StrikeUp]),
    notional: cleanupNumber(row[ColTargetPivot.Notional], true),
    leverageAmount: liftToOptionalString(cleanupNumber, true)(row[ColTargetPivot.LeverageAmount]),
  });

export const requiredPivotTargetColumns = [
  'Fixing date',
  'Settlement date',
  'Strike up',
  'PIVOT',
  'Strike down',
  'Notional',
  'Leverage',
];

// ███████╗██╗  ██╗██╗    ████████╗ █████╗ ██████╗  ██████╗ ███████╗████████╗
// ██╔════╝██║ ██╔╝██║    ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝ ██╔════╝╚══██╔══╝
// █████╗  █████╔╝ ██║       ██║   ███████║██████╔╝██║  ███╗█████╗     ██║
// ██╔══╝  ██╔═██╗ ██║       ██║   ██╔══██║██╔══██╗██║   ██║██╔══╝     ██║
// ███████╗██║  ██╗██║       ██║   ██║  ██║██║  ██║╚██████╔╝███████╗   ██║
// ╚══════╝╚═╝  ╚═╝╚═╝       ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝   ╚═╝

enum ColTargetEki {
  FixingDate = 0,
  SettlementDate = 1,
  EkiTrigger = 2,
  Strike = 3,
  Notional = 4,
  LeverageAmount = 5,
}

export type ImportedTargetEkiScheduleRow = {
  step: string;
} & ImportedTargetVanillaScheduleRow;

export const mapTargetEkiScheduleRow =
  ({ cleanupNumber, sanitizeDate }: Cleaners) =>
  (row: readonly any[]): ImportedTargetEkiScheduleRow => ({
    fixingDate: sanitizeDate(row[ColTargetEki.FixingDate]),
    settlementDate: liftToOptionalString(sanitizeDate)(row[ColTargetEki.SettlementDate]),
    step: cleanupNumber(row[ColTargetEki.EkiTrigger]),
    strike: cleanupNumber(row[ColTargetEki.Strike]),
    notional: cleanupNumber(row[ColTargetEki.Notional], true),
    leverageAmount: liftToOptionalString(cleanupNumber, true)(row[ColTargetEki.LeverageAmount]),
  });

export const requiredEkiTargetColumns = ['Fixing date', 'Settlement date', 'EKI', 'Strike', 'Notional', 'Leverage'];

// ███████╗██╗  ██╗██╗    ██████╗ ██╗██╗   ██╗ ██████╗ ████████╗    ████████╗ █████╗ ██████╗  ██████╗ ███████╗████████╗
// ██╔════╝██║ ██╔╝██║    ██╔══██╗██║██║   ██║██╔═══██╗╚══██╔══╝    ╚══██╔══╝██╔══██╗██╔══██╗██╔════╝ ██╔════╝╚══██╔══╝
// █████╗  █████╔╝ ██║    ██████╔╝██║██║   ██║██║   ██║   ██║          ██║   ███████║██████╔╝██║  ███╗█████╗     ██║
// ██╔══╝  ██╔═██╗ ██║    ██╔═══╝ ██║╚██╗ ██╔╝██║   ██║   ██║          ██║   ██╔══██║██╔══██╗██║   ██║██╔══╝     ██║
// ███████╗██║  ██╗██║    ██║     ██║ ╚████╔╝ ╚██████╔╝   ██║          ██║   ██║  ██║██║  ██║╚██████╔╝███████╗   ██║
// ╚══════╝╚═╝  ╚═╝╚═╝    ╚═╝     ╚═╝  ╚═══╝   ╚═════╝    ╚═╝          ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝   ╚═╝

enum ColTargetEkiPivot {
  FixingDate = 0,
  SettlementDate = 1,
  EkiUp = 2,
  StrikeUp = 3,
  Pivot = 4,
  StrikeDown = 5,
  EkiDown = 6,
  Notional = 7,
  LeverageAmount = 8,
}

export interface ImportedTargetEkiPivotScheduleRow {
  fixingDate: string;
  settlementDate: string | undefined;
  ekiUp: string;
  strikeUp: string;
  step: string;
  strikeDown: string;
  ekiDown: string;
  notional: string;
  leverageAmount: string | undefined;
}

export const mapTargetEkiPivotScheduleRow =
  ({ cleanupNumber, sanitizeDate }: Cleaners) =>
  (row: readonly any[]): ImportedTargetEkiPivotScheduleRow => ({
    fixingDate: sanitizeDate(row[ColTargetEkiPivot.FixingDate]),
    settlementDate: liftToOptionalString(sanitizeDate)(row[ColTargetEkiPivot.SettlementDate]),
    ekiUp: cleanupNumber(row[ColTargetEkiPivot.EkiUp]),
    ekiDown: cleanupNumber(row[ColTargetEkiPivot.EkiDown]),
    step: cleanupNumber(row[ColTargetEkiPivot.Pivot]),
    strikeDown: cleanupNumber(row[ColTargetEkiPivot.StrikeDown]),
    strikeUp: cleanupNumber(row[ColTargetEkiPivot.StrikeUp]),
    notional: cleanupNumber(row[ColTargetEkiPivot.Notional], true),
    leverageAmount: liftToOptionalString(cleanupNumber, true)(row[ColTargetEkiPivot.LeverageAmount]),
  });

export const requiredPivotEkiTargetColumns = [
  'Fixing date',
  'Settlement date',
  'EKI up',
  'Strike up',
  'PIVOT',
  'Strike down',
  'EKI down',
  'Notional',
  'Leverage',
];

// ███████╗ ██████╗ ██████╗ ██╗    ██╗ █████╗ ██████╗ ██████╗      █████╗  ██████╗ ██████╗██╗   ██╗███╗   ███╗██╗   ██╗██╗      █████╗ ████████╗ ██████╗ ██████╗
// ██╔════╝██╔═══██╗██╔══██╗██║    ██║██╔══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔════╝██╔════╝██║   ██║████╗ ████║██║   ██║██║     ██╔══██╗╚══██╔══╝██╔═══██╗██╔══██╗
// █████╗  ██║   ██║██████╔╝██║ █╗ ██║███████║██████╔╝██║  ██║    ███████║██║     ██║     ██║   ██║██╔████╔██║██║   ██║██║     ███████║   ██║   ██║   ██║██████╔╝
// ██╔══╝  ██║   ██║██╔══██╗██║███╗██║██╔══██║██╔══██╗██║  ██║    ██╔══██║██║     ██║     ██║   ██║██║╚██╔╝██║██║   ██║██║     ██╔══██║   ██║   ██║   ██║██╔══██╗
// ██║     ╚██████╔╝██║  ██║╚███╔███╔╝██║  ██║██║  ██║██████╔╝    ██║  ██║╚██████╗╚██████╗╚██████╔╝██║ ╚═╝ ██║╚██████╔╝███████╗██║  ██║   ██║   ╚██████╔╝██║  ██║
// ╚═╝      ╚═════╝ ╚═╝  ╚═╝ ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝     ╚═╝  ╚═╝ ╚═════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝ ╚═════╝ ╚══════╝╚═╝  ╚═╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝

enum ColForwardAccu {
  FixingDate = 0,
  SettlementDate = 1,
  EKI = 2,
  Strike = 3,
  Step = 4,
  Notional = 5,
  LeverageAmount = 6,
}

export type ImportedFowardScheduleRow = {
  fixingDate: string;
  settlementDate: string | undefined;
  // ekiTrigger: string;
  // step: string;
  // strike: string;
  // notional: string;
  // leverageAmount: string | undefined;
};

export const mapForwardScheduleRow =
  ({ sanitizeDate }: Cleaners) =>
  (row: readonly any[]): ImportedFowardScheduleRow => {
    return {
      fixingDate: sanitizeDate(row[ColForwardAccu.FixingDate]),
      settlementDate: liftToOptionalString(sanitizeDate)(row[ColForwardAccu.SettlementDate]),
      // ekiTrigger: cleanupNumber(row[ColForwardAccu.EKI]),
      // strike: cleanupNumber(row[ColForwardAccu.Strike]),
      // step: cleanupNumber(row[ColForwardAccu.Step]),
      // notional: cleanupNumber(row[ColForwardAccu.Notional], true),
      // leverageAmount: liftToOptionalString(cleanupNumber, true)(row[ColForwardAccu.LeverageAmount]),
    };
  };
export const mapForwardScheduleToFixing = (row: ImportedScheduledRow): ForwardAccumulatorScheduleLine => {
  return {
    fixingDate: row.fixingDate,
    paymentDate: row.settlementDate,
    // ekiTrigger: row.ekiTrigger,
    // strike: row.strike,
    // step: row.step,
    // notional: row.notional,
    // leverageAmount: row.leverageAmount,
  } as ForwardAccumulatorScheduleLine;
};

export const requiredForwardColumns = ['Fixing date', 'Settlement date'];

export const getRequiredColumns = (accuType: AccumulatorType | undefined): string[] => {
  switch (accuType) {
    case 'TargetAccu':
      return requiredVanillaTargetColumns;
    case 'EKI':
      return requiredEkiTargetColumns;
    case 'PIVOT':
      return requiredPivotTargetColumns;
    case 'PIVOTEKI':
      return requiredPivotEkiTargetColumns;
    case 'Permanent':
    case 'Standard':
      return requiredForwardColumns;
    default:
      return requiredVanillaTargetColumns;
  }
};

export const mapScheduleRowToFixing = (
  row: ImportedScheduledRow,
  accuType: AccumulatorType | undefined,
): AccumulatorScheduleLine => {
  if (isTargetVanillaAccu(row, accuType)) {
    return {
      fixingDate: row.fixingDate,
      paymentDate: row.settlementDate,
      strike: parseNumber(row.strike),
      amount: parseNumber(row.notional),
      leverageAmount: parseNumber(row.leverageAmount),
    } as AccumulatorScheduleLine;
  }

  if (isTargetPivotAccu(row, accuType)) {
    return {
      fixingDate: row.fixingDate,
      paymentDate: row.settlementDate,
      strikeUp: parseNumber(row?.strikeUp),
      pivot: parseNumber(row?.step),
      strikeDown: parseNumber(row?.strikeDown),
      amount: parseNumber(row.notional),
      leverageAmount: parseNumber(row.leverageAmount),
    } as AccumulatorScheduleLine;
  }

  if (isTargetEkiPivotAccu(row, accuType)) {
    return {
      fixingDate: row.fixingDate,
      paymentDate: row.settlementDate,
      strikeDown: parseNumber(row?.strikeDown),
      strikeUp: parseNumber(row?.strikeUp),
      pivot: parseNumber(row?.step),
      ekiDown: parseNumber(row?.ekiDown),
      ekiUp: parseNumber(row?.ekiUp),
      amount: parseNumber(row.notional),
      leverageAmount: parseNumber(row.leverageAmount),
    } as AccumulatorScheduleLine;
  }

  if (isTargetEkiAccu(row, accuType)) {
    return {
      fixingDate: row.fixingDate,
      paymentDate: row.settlementDate,
      strike: parseNumber(row.strike),
      amount: parseNumber(row.notional),
      leverageAmount: parseNumber(row.leverageAmount),
      ekiTrigger: parseNumber(row?.step),
    } as AccumulatorScheduleLine;
  }

  return {
    fixingDate: row.fixingDate,
    paymentDate: row.settlementDate,
  } as AccumulatorScheduleLine;
};

function isTargetVanillaAccu(
  row: ImportedScheduledRow,
  accuType: AccumulatorType | undefined,
): row is ImportedTargetVanillaScheduleRow {
  return row && accuType === 'TargetAccu';
}

function isTargetPivotAccu(
  row: ImportedScheduledRow,
  accuType: AccumulatorType | undefined,
): row is ImportedTargetPivotScheduleRow {
  return row && accuType === 'PIVOT';
}

function isTargetEkiPivotAccu(
  row: ImportedScheduledRow,
  accuType: AccumulatorType | undefined,
): row is ImportedTargetEkiPivotScheduleRow {
  return row && accuType === 'PIVOTEKI';
}

function isTargetEkiAccu(
  row: ImportedScheduledRow,
  accuType: AccumulatorType | undefined,
): row is ImportedTargetEkiScheduleRow {
  return row && accuType === 'EKI';
}
